import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { parseRowsToQuotes, type Quote } from "features/market/lib/quotes";
import type { QuoteCurrency } from "features/market/lib/marketQuotes";
import { fetchCotizaciones } from "features/market/api/MarketQuotes";
import type { PanelType, PanelTypeWithCrypto } from "features/market/api/MarketQuotes";
import { useUserSession } from "shared/contexts/UserContext";

const RATE_LIMIT_MS = 60 * 1000;
const QUOTE_CURRENCY_STORAGE_KEY = "quoteCurrencyView";
const AUTO_REFRESH_MS = 10 * 60 * 1000;
let rateLimitUntil = 0;
const rateLimitListeners = new Set<() => void>();

function notifyRateLimitChange(): void {
  rateLimitListeners.forEach((listener) => listener());
}
const PANEL_TYPES: PanelTypeWithCrypto[] = ["acciones", "bonos", "letras", "cedears", "on", "crypto"];

interface PanelQuotes {
  quotes: Quote[];
  updatedAt: string;
}

interface PanelState {
  data: PanelQuotes | null;
  loading: boolean;
  error: string | null;
  updatedAt: number;
}

type QuotesState = Record<PanelTypeWithCrypto, PanelState>;

let externalQuotesState: QuotesState;
const quotePanelListeners = new Map<PanelTypeWithCrypto, Set<() => void>>();
const quotePanelKeys: PanelTypeWithCrypto[] = [...PANEL_TYPES, "crypto"];

function initialPanelState(): PanelState {
  return {
    data: null,
    loading: false,
    error: null,
    updatedAt: 0,
  };
}

function initialState(): QuotesState {
  return PANEL_TYPES.reduce(
    (acc, p) => {
      acc[p] = initialPanelState();
      return acc;
    },
    {} as QuotesState
  );
}

externalQuotesState = initialState();

function publishExternalPanel(panel: PanelTypeWithCrypto, next: PanelState): void {
  if (externalQuotesState[panel] === next) return;
  externalQuotesState = { ...externalQuotesState, [panel]: next };
  quotePanelListeners.get(panel)?.forEach((listener) => listener());
}

/** Subscribes to exactly the market panels used by the current view. */
export function useMarketQuotePanels(panels: readonly PanelTypeWithCrypto[]): Partial<QuotesState> {
  const key = Array.from(new Set(panels)).sort().join(",");
  const selectedPanels = useMemo(() => key ? key.split(",") as PanelTypeWithCrypto[] : [], [key]);
  const snapshotRef = useRef<Partial<QuotesState>>({});
  const subscribe = useCallback((listener: () => void) => {
    selectedPanels.forEach((panel) => {
      const listeners = quotePanelListeners.get(panel) ?? new Set<() => void>();
      listeners.add(listener);
      quotePanelListeners.set(panel, listeners);
    });
    return () => selectedPanels.forEach((panel) => quotePanelListeners.get(panel)?.delete(listener));
  }, [selectedPanels]);
  const getSnapshot = useCallback(() => {
    let changed = false;
    const previous = snapshotRef.current;
    const next: Partial<QuotesState> = {};
    selectedPanels.forEach((panel) => {
      next[panel] = externalQuotesState[panel];
      if (previous[panel] !== next[panel]) changed = true;
    });
    if (changed || Object.keys(previous).length !== selectedPanels.length) snapshotRef.current = next;
    return snapshotRef.current;
  }, [selectedPanels]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function loadQuoteCurrency(): QuoteCurrency {
  try {
    const v = localStorage.getItem(QUOTE_CURRENCY_STORAGE_KEY);
    if (v === "ARS" || v === "USD") return v;
  } catch (_) {}
  return "ARS";
}

interface MarketQuotesContextValue {
  getQuotes: (panel: PanelTypeWithCrypto) => PanelQuotes | null;
  refreshQuotes: (panel?: PanelTypeWithCrypto) => Promise<void>;
  loading: (panel: PanelTypeWithCrypto) => boolean;
  error: (panel: PanelTypeWithCrypto) => string | null;
  lastRefreshAt: (panel: PanelTypeWithCrypto) => number;
  requestLoad: (panel: PanelTypeWithCrypto) => void;
  quoteCurrencyView: QuoteCurrency;
  setQuoteCurrencyView: (v: QuoteCurrency) => void;
}

const MarketQuotesContext = createContext<MarketQuotesContextValue | null>(null);

export function MarketQuotesProvider({ children }: PropsWithChildren) {
  const { profile } = useUserSession();
  const [state, setState] = useState<QuotesState>(initialState);
  const sessionTokenRef = useRef(profile.token);
  sessionTokenRef.current = profile.token;
  const [quoteCurrencyView, setQuoteCurrencyState] = useState<QuoteCurrency>(loadQuoteCurrency);
  const lastRefreshRef = useRef<Record<string, number>>({});
  const stateRef = useRef<QuotesState>(initialState());
  const previousTokenRef = useRef(profile.token);
  const requestedPanelsRef = useRef<Set<PanelTypeWithCrypto>>(new Set());
  const autoRefreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const updatePanel = useCallback((panel: PanelTypeWithCrypto, update: (current: PanelState) => PanelState) => {
    const next = { ...stateRef.current, [panel]: update(stateRef.current[panel]) };
    stateRef.current = next;
    setState(next);
  }, []);

  const fetchPanel = useCallback(async (panel: PanelTypeWithCrypto) => {
    const tokenAtStart = sessionTokenRef.current;
    if (panel === "crypto") {
      updatePanel("crypto", () => ({ data: { quotes: [], updatedAt: "" }, loading: false, error: null, updatedAt: Date.now() }));
      return;
    }
    if (stateRef.current[panel].loading) return;
    updatePanel(panel, (current) => ({ ...current, loading: true, error: null }));
    try {
      const res = await fetchCotizaciones(panel as PanelType);
      if (sessionTokenRef.current !== tokenAtStart) return;
      const quotes = parseRowsToQuotes(res.rows, panel);
      updatePanel(panel, () => ({
          data: { quotes, updatedAt: res.updatedAt },
          loading: false,
          error: null,
          updatedAt: Date.now(),
        }));
      lastRefreshRef.current[panel] = Date.now();
    } catch (err) {
      if (sessionTokenRef.current !== tokenAtStart) return;
      updatePanel(panel, (current) => ({
          ...current,
          loading: false,
          error: String(err),
        }));
    }
  }, [updatePanel]);

  const refreshQuotes = useCallback(
    async (panel?: PanelTypeWithCrypto) => {
      const now = Date.now();
      if (now < rateLimitUntil) {
        notifyRateLimitChange();
        return;
      }
      rateLimitUntil = now + RATE_LIMIT_MS;
      notifyRateLimitChange();

      const panelsToRefresh: PanelTypeWithCrypto[] = panel
        ? [panel]
        : Array.from(requestedPanelsRef.current);
      if (panelsToRefresh.length === 0) {
        PANEL_TYPES.forEach((p) => requestedPanelsRef.current.add(p));
        await Promise.all(PANEL_TYPES.map((p) => fetchPanel(p)));
      } else {
        await Promise.all(panelsToRefresh.map((p) => fetchPanel(p)));
      }
    },
    [fetchPanel]
  );

  const requestLoad = useCallback(
    (panel: PanelTypeWithCrypto) => {
      requestedPanelsRef.current.add(panel);
      const current = stateRef.current[panel];
      if (!current.data && !current.loading && !current.error) {
        fetchPanel(panel);
      }
    },
    [fetchPanel]
  );

  useEffect(() => {
    stateRef.current = state;
    if (previousTokenRef.current !== profile.token) {
      previousTokenRef.current = profile.token;
      const empty = initialState();
      stateRef.current = empty;
      externalQuotesState = empty;
      setState(empty);
      quotePanelKeys.forEach((panel) => quotePanelListeners.get(panel)?.forEach((listener) => listener()));
      return;
    }
    quotePanelKeys.forEach((panel) => publishExternalPanel(panel, state[panel]));
  }, [state, profile.token]);

  useEffect(() => {
    try {
      localStorage.setItem(QUOTE_CURRENCY_STORAGE_KEY, quoteCurrencyView);
    } catch (_) {}
  }, [quoteCurrencyView]);

  const setQuoteCurrencyView = useCallback((v: QuoteCurrency) => {
    setQuoteCurrencyState(v);
  }, []);

  useEffect(() => {
    if (!profile.token) {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
        autoRefreshTimerRef.current = null;
      }
      return;
    }
    autoRefreshTimerRef.current = setInterval(() => {
      if (requestedPanelsRef.current.size > 0) {
        refreshQuotes();
      }
    }, AUTO_REFRESH_MS);
    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
    };
  }, [profile.token, refreshQuotes]);

  const getQuotes = useCallback((panel: PanelTypeWithCrypto) => externalQuotesState[panel]?.data ?? null, []);
  const loading = useCallback((panel: PanelTypeWithCrypto) => externalQuotesState[panel]?.loading ?? false, []);
  const error = useCallback((panel: PanelTypeWithCrypto) => externalQuotesState[panel]?.error ?? null, []);
  const lastRefreshAt = useCallback((panel: PanelTypeWithCrypto) => externalQuotesState[panel]?.updatedAt ?? 0, []);
  const value = useMemo<MarketQuotesContextValue>(() => ({
    getQuotes,
    refreshQuotes,
    loading,
    error,
    lastRefreshAt,
    requestLoad,
    quoteCurrencyView,
    setQuoteCurrencyView,
  }), [getQuotes, refreshQuotes, loading, error, lastRefreshAt, requestLoad, quoteCurrencyView, setQuoteCurrencyView]);

  return (
    <MarketQuotesContext.Provider value={value}>
      {children}
    </MarketQuotesContext.Provider>
  );
}

/** A one-second countdown subscription used only by the refresh control. */
export function useRateLimitRemaining(): number {
  const [remaining, setRemaining] = useState(() => Math.max(0, Math.ceil((rateLimitUntil - Date.now()) / 1000)));
  const isRateLimited = remaining > 0;

  useEffect(() => {
    const update = () => setRemaining(Math.max(0, Math.ceil((rateLimitUntil - Date.now()) / 1000)));
    rateLimitListeners.add(update);
    update();
    return () => { rateLimitListeners.delete(update); };
  }, []);

  useEffect(() => {
    if (!isRateLimited) return undefined;
    const id = setInterval(() => {
      setRemaining(Math.max(0, Math.ceil((rateLimitUntil - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [isRateLimited]);

  return remaining;
}

export function useMarketQuotesContext(): MarketQuotesContextValue {
  const ctx = useContext(MarketQuotesContext);
  if (!ctx) {
    throw new Error("useMarketQuotesContext must be used within MarketQuotesProvider");
  }
  return ctx;
}
